import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FilterOperation, filterOperations } from '../../filter/filter-operation';
import { QueryComponent } from '../query-component';
import { Condition, DataField, Operation, Query } from '@deckplan.io/common/filters';
import { filter, map, startWith } from 'rxjs/operators';
import { merge, Observable } from 'rxjs';
import { CardSymbologyUtils } from '../../../cards/card-symbology-utils';

@Component({
    selector: 'app-condition',
    templateUrl: './condition.component.html',
    styleUrls: ['./condition.component.scss']
})
export class ConditionComponent implements OnInit, QueryComponent {

    @Input()
    public editable = true;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    @Input() set Columns(newColumns: DataField[]) {
        if (newColumns === this.columns) {
            return;
        }

        this.columns = newColumns;

        // Every time the columns are changed, we need to re-create the columns validator
        this.formGroup.get('field').setValidators([Validators.required, ConditionComponent.valueInSetValidator(this.columns)]);
    }

    @Input() set condition(condition: Condition) {
        if (condition) {
            if (condition.dataField) {
                this.formGroup.get('field').setValue(condition.dataField);
            }
            this.formGroup.get('operation').setValue(condition.operation);
            this.formGroup.get('value').setValue(condition.value);
        }
    }

    @Output() public conditionUpdated = new EventEmitter<Condition>();

    @Output() public remove = new EventEmitter();

    public columns: DataField[] = [];
    public formGroup: FormGroup;
    public columnsToSearch: Observable<DataField[]>;
    public searchOperations: Observable<Operation[]>;
    public selectedColors = [];
    public costSymbol = '';

    constructor(public readonly cardSymbologyUtils: CardSymbologyUtils, private readonly fb: FormBuilder) {
        this.formGroup = this.fb.group({
            field: ['', [Validators.required, ConditionComponent.valueInSetValidator(this.columns)]],
            operation: ['', [Validators.required, ConditionComponent.valueInSetValidator(filterOperations)]],
            value: ['']
        });
    }

    /// Provides a validator function that returns valid if the value in the form is one of those in the
    /// set provided when the validator function was generated. Might make sense to pull out into its own location
    /// within the generic module.
    public static valueInSetValidator(validValues: readonly any[]): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const valueIsValid = validValues.some(item => item === control.value);
            return valueIsValid ? null : {valueNotInSet: {value: control.value}};
        };
    }

    public ngOnInit(): void {

        // Sets up filtering of the autocomplete for the 'field' form field to only items that we were given in our Columns input
        this.columnsToSearch = this.formGroup.get('field').valueChanges.pipe(
            filter(value => value !== null),
            startWith(''),
            map(value => typeof (value) === 'string' ? value : value.title),
            map(value => this.filterColumns(value))
        );

        // Sets up filtering of the autocomplete for the 'operation' form field to only be FilterOperation options
        this.searchOperations = this.formGroup.get('operation').valueChanges.pipe(
            startWith(''),
            map(value => this.filterOperations(value))
        );

        this.formGroup.get('field').valueChanges.subscribe(value => {
            if (value.dataType === 'mana') {
                this.formGroup.get('operation').setValue('includes');
                this.formGroup.get('operation').disable();
            } else if (value.dataType === 'color') {
                this.formGroup.get('operation').setValue('is in color identity');
                this.formGroup.get('operation').disable();
            } else {
                this.formGroup.get('operation').enable();
            }
            this.selectedColors = [];
        });

        merge(
            this.formGroup.get('field').valueChanges,
            this.formGroup.get('operation').valueChanges,
            this.formGroup.get('value').valueChanges,
        ).subscribe(() => {
            this.conditionUpdated.emit(this.toCondition());
        });

        if (!this.editable) {
            this.formGroup.get('field').disable();
            this.formGroup.get('operation').disable();
            this.formGroup.get('value').disable();
        }
    }

    /// Used in the 'field' autocomplete filter
    private filterColumns(value: string): DataField[] {
        const filterValue = value.toLowerCase();

        return this.columns.filter(column => column.title.toLowerCase().includes(filterValue));
    }

    /// Used in the 'operation' autocomplete filter
    private filterOperations(value: string): FilterOperation[] {
        return filterOperations.filter(operation => {
            if (this.isManaOrColorColumn(this.formGroup.get('field').value)) {
                return operation.toLowerCase().includes(value) && (value === '=' || value === '!=' || value === 'includes');
            }
            return operation.toLowerCase().includes(value);
        });
    }

    public toQuery(): Query {
        if (this.formGroup.invalid || this.formGroup.pristine) {
            return null;
        }

        return this.toCondition();
    }

    private toCondition(): Condition {
        let value = this.formGroup.get('value').value;
        if (this.formGroup.get('field').value.dataType === 'mana' && this.costSymbol.length) {
            value = this.costSymbol + '%' + value;
        }
        return {
            value,
            operation: this.formGroup.get('operation').value,
            dataField: this.formGroup.get('field').value,
            objectType: this.formGroup.get('field').value.objectType
        } as Condition;
    }

    /// Used in the 'field' form field to display the pretty name of the Column
    public columnDisplay(column: DataField): string {
        return column && column.title ? column.title : '';
    }

    /// Sets the 'field' form field to have the actual Column object in it if the user
    /// has typed an exact name, rather than picking one from the list. Makes things a bit more
    /// user-friendly in not requiring the user to pick from the drop-down with the mouse or arrows.
    public selectFieldFromName() {
        const fieldValue = this.formGroup.get('field').value;
        if (fieldValue === null || fieldValue === undefined || fieldValue.length === 0 || this.columns.some(column => column === fieldValue)) {
            return;
        }

        // Locate the Column with the name that we have, if there is one.
        const matchingColumns = this.columns.filter(column => column.title === fieldValue);
        if (matchingColumns.length === 0) {
            return;
        }

        // Should now only have one that is an exact match, and if not, we can't tell the difference so just pick the first
        // Now that we've located a matching column, set the field to have the Column in it, rather than just its name
        this.formGroup.get('field').setValue(matchingColumns[0]);
    }

    public removeMe() {
        this.remove.emit();
    }

    public isManaOrColorColumn(column: DataField) {
        return column && (column.dataType === 'mana' || column.dataType === 'color');
    }

    public inSelectedColors(symbol: string) {
        return this.selectedColors.includes(symbol);
    }

    public toggleSelectedSymbol(symbol: string) {
        const index = this.selectedColors.findIndex(sym => sym === symbol);
        if (index >= 0) {
            this.selectedColors.splice(index, 1);
        } else {
            this.selectedColors.push(symbol);
        }

        const colorValue = this.getColorSort();

        // Order: 'W','U','B','R','G','C' for Mana Cost
        // Order: 'B','G','R','U','W','C" for Color Identity
        this.selectedColors = this.selectedColors.sort((a, b) => colorValue[a] - colorValue[b]);

        if (this.formGroup.get('field').value.dataType === 'mana') {
            this.formGroup.get('value').setValue(this.selectedColors.length ? this.selectedColors.map(this.stripSymbol.bind(this)).join('%') : '');
        } else {
            this.formGroup.get('value').setValue(this.selectedColors.map(this.stripSymbol.bind(this)));
        }
    }

    private getColorSort() {
        if (this.formGroup.get('field').value.dataType === 'mana') {
            return {
                '{W}': 0,
                '{U}': 1,
                '{B}': 2,
                '{R}': 3,
                '{G}': 4,
                '{C}': 5
            };
        } else {
            return {
                '{B}': 0,
                '{G}': 1,
                '{R}': 2,
                '{U}': 3,
                '{W}': 4,
                '{C}': 5
            };
        }
    }

    private stripSymbol(symbol: string): string {
        // Symbols are surrounded by braces, so match for those
        const manaRegex = /\{(\w+?)\}/g;

        const regexResults = manaRegex.exec(symbol);
        return regexResults[1];
    }

}
